import React from 'react';

import Layout from '~/components/layout/layouts/yrbs';
import Seo from '~/components/util/seo';
import YrbsJumbotron from '~/components/modules/sites/yrbs/jumbotron';
import Section from '~/components/modules/section';
import Title from '~/components/modules/text/title';
import Subhead from '~/components/modules/text/subhead';
import Button from '~/components/modules/button';
import YrbsStatsSection from '~/components/modules/sites/yrbs/stats';
import Icon from '~/components/modules/icon';
import Card from '~/components/modules/cards/default';
import JotformEmbed from '~/components/modules/jotform-embed';

import useSectionTracker from '~/hooks/useSectionTracker';

import statistics from '~/data/yrbs/statistics';
import factsheets from '~/data/yrbs/factsheets';

const YrbsPage = ({ location }) => {
	const setCurrentSection = useSectionTracker();

	return (
		<Layout
			location={location}
			jumbotron={
				<YrbsJumbotron
					title="The Youth Risk Behavior Survey (YRBS)"
					description={<>Shedding light on student behaviors. Helping to keep our <span className="whitespace-nowrap">schools safe.</span></>}
					image="yrbs-smoke"
					imageAlt="Teen sitting with smoke in the background"
				/>
			}
			pageAnchors={[
				{
					id: 'intro',
					name: 'Intro',
					hidden: true
				},
				{
					id: 'statistics',
					name: 'Statistics'
				},
				{
					id: 'students',
					name: 'Students'
				},
				{
					id: 'admins-and-teachers',
					name: 'School Admins and Teachers'
				},
				{
					id: 'get-involved',
					name: 'Get Involved'
				},
				{
					id: 'learn-more',
					name: 'More about YRBS'
				},
				{
					id: 'contact',
					name: 'Contact Us'
				}
			]}
		>
			<Seo
				title="The Youth Risk Behavior Survey (YRBS)"
				description="The Youth Risk Behavior Survey (YRBS) is shedding light on student behaviors. Helping to keep our schools safe."
			/>

			<Section
				id="intro"
				className="container max-w-2xl px-4 my-12 text-center"
				setCurrentSection={setCurrentSection}
			>
				<Title>The YRBS is a key part of <span className="whitespace-nowrap">student outreach.</span></Title>
				<p>This anonymous survey is taken every two years by the Centers for Disease Control and Prevention. It helps our schools track students’ physical, emotional, and physiological health, collecting data in key behavioral <span className="whitespace-nowrap">areas including:</span></p>

				<ul className="list-disc grid grid-cols-1 sm:grid-cols-2 auto-rows-auto gap-x-4 gap-y-2 px-8 mt-6 mb-8 text-left">
					<li>Unintentional injuries and violence</li>
					<li>Tobacco and e-cigarette (vape) use</li>
					<li>Alcohol and other drug use</li>
					<li>Sexual and dietary behaviors</li>
					<li>Physical activity</li>
				</ul>

				<i className="block h-1 my-8 mx-auto max-w-sm bg-gray-250" />

				<h3 className="mb-6 font-serif text-indigo-950 font-bold text-xl">Thanks to the results of the YRBS, we’re better equipped to identify progress and shortcomings, determine proper allocation of resources, and make a difference in <span className="whitespace-nowrap">students’ lives.</span></h3>

				<Button
					as="scroll"
					to="contact"
					variant="primary"
				>
					Have questions?
				</Button>
			</Section>

			<YrbsStatsSection
				setCurrentSection={setCurrentSection}
				statistics={statistics}
				factsheets={factsheets}
			/>

			<Section
				id="students"
				className="container px-4 my-12 text-center"
				setCurrentSection={setCurrentSection}
			>
				<div className="max-w-lg mx-auto mb-10">
					<Title>How does the YRBS <span className="whitespace-nowrap">help students?</span></Title>
					<p>This data directly serves schools and state agencies — but behind it all is a mission to reduce risky behavior and give students a better foundation for a safe, bright, and <span className="whitespace-nowrap">healthy future.</span></p>
				</div>

				<div className="grid auto-rows-auto grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
					{[
						{
							id: 'environment',
							icon: 'teenyicons:school-outline',
							text: 'Supports a safer learning environment.'
						},
						{
							id: 'injury',
							icon: 'tabler:hand-stop',
							text: 'Prevents injury, violence, and risky behavior.'
						},
						{
							id: 'struggling',
							icon: 'ri:mental-health-line',
							text: 'Reaches students who are struggling.'
						},
						{
							id: 'success',
							icon: 'stash:graduation-cap',
							text: 'Sets students up for future success.'
						}
					].map(item => (
						<div
							key={item.id}
							className="text-center"
						>
							<Icon
								iconImage={item.icon}
								sizeClasses="w-12 h-12"
								className="mx-auto text-emerald-550"
							/>
							<p className="mt-2 font-bold text-sm [text-wrap:pretty]">{item.text}</p>
						</div>
					))}
				</div>
			</Section>

			<Section
				id="admins-and-teachers"
				className="py-12 bg-emerald-150"
				setCurrentSection={setCurrentSection}
			>
				<div className="container px-4">
					<div className="max-w-lg mx-auto mb-10 text-center">
						<Title>How does the YRBS help school admins <span className="whitespace-nowrap">and teachers?</span></Title>
						<p>The YRBS is a powerful tool for guiding public health as well as each individual school’s efforts, ensuring that we provide the necessary resources to prevent behaviors that put students <span className="whitespace-nowrap">at risk.</span></p>
					</div>

					<div className="grid auto-rows-auto grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
						{[
							'Spots trends and emerging issues.',
							'Tracks progress of existing outreach.',
							'Identifies disparities among different demographics.',
							'Influences state and community agencies on new and existing programs and policies.',
							'Helps determine where state resources should be allocated.',
							'Assists in requesting grant funding.',
							'Contributes to a scientific understanding of adolescent health factors.',
							'Alerts schools when critical intervention is needed.'
						].map(item => (
							<Card
								key={item}
								variant="white"
								cardBodyClasses="flex flex-col justify-center px-3 py-2 border-l-[8px] border-emerald-650"
							>
								<p className="font-bold text-xs [text-wrap:pretty]">{item}</p>
							</Card>
						))}
					</div>
				</div>
			</Section>

			<Section
				id="get-involved"
				className="container px-4 my-12 text-center"
				setCurrentSection={setCurrentSection}
			>
				<div className="max-w-2xl mx-auto mb-10">
					<Subhead>Take Action</Subhead>
					<Title>How can schools <span className="whitespace-nowrap">get involved?</span></Title>
					<p className="mb-4">If your school is not already participating in the YRBS, it should. Without school participation in the survey, Delaware is at a significant disadvantage for public health planning <span className="whitespace-nowrap">and surveillance.</span></p>
					<p>If your school is among those selected to participate, please do so. You’ll help to ensure continued prevention funding as well as ongoing progress in addressing negative behaviors and their consequences. It’s all with the goal of ensuring brighter, healthier futures for <span className="whitespace-nowrap">our children.</span></p>
				</div>
			</Section>

			<Section
				id="learn-more"
				className="py-12 bg-gray-150"
				setCurrentSection={setCurrentSection}
			>
				<div className="container max-w-4xl px-4">
					<Title className="text-center">Learn more about <span className="whitespace-nowrap">the YRBS:</span></Title>

					<div className="grid auto-rows-auto grid-cols-1 md:grid-cols-2 gap-4">
						{[
							{
								id: 'dhss',
								url: 'https://dhss.delaware.gov/dph/dpc/yrbs.html',
								title: 'Delaware Division of Public Health',
								description: 'Additional information about the YRBS, especially as it pertains to the state of Delaware, provided by Delaware Department of Health and Social Services.'
							},
							{
								id: 'cdc',
								url: 'https://www.cdc.gov/yrbs/index.html',
								title: 'The Centers for Disease Control and Prevention',
								description: 'The comprehensive informational destination for all things YRBS, provided by the CDC — the agency that administers the YRBS.'
							}
						].map(item => (
							<Card
								key={item.id}
								as="a"
								to={item.url}
								variant="none"
								className="!shadow-none"
								cardBodyClasses="px-4 py-2.5"
							>
								<h4 className="mb-2 text-emerald-550 font-bold underline [text-wrap:pretty] transition-colors group-hover:text-indigo-950 group-hover:no-underline group-focus:text-indigo-950 group-focus:no-underline">{item.title}</h4>
								<p className="[text-wrap:pretty]">{item.description}</p>
							</Card>
						))}
					</div>
				</div>
			</Section>

			<Section
				id="contact"
				className="py-12 bg-indigo-150"
				setCurrentSection={setCurrentSection}
			>
				<div className="container px-4">
					<div className="max-w-lg mx-auto text-center mb-10">
						<Title>Contact Us.</Title>
						<p className="[text-wrap:pretty]">Have questions? Reach out using the form below, and the Division of Public Health will get back to you as soon as we can.</p>
					</div>
					<JotformEmbed src="https://form.jotform.com/242966187861067" />
				</div>
			</Section>
		</Layout>
	);
};

export default YrbsPage;

